import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Button, Card, StatusIndicator, type StatusTone } from "../components/ui";
import { ContextBar } from "../components/context-bar";
import { KeyVal, StatePanel, StateTag, formatAge, shortHash } from "../components/phase3";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  type BotInstance,
  type NormalizedChannel,
  type NormalizedSnapshotContent,
  type SnapshotDetail,
  type SnapshotStatus,
  type SnapshotSummary,
} from "../lib/api";

/**
 * What to tell someone whose snapshot did not happen.
 *
 * The API writes a real sentence for the cases it knows — "ODIN is not in this
 * Discord server… invite ODIN back and try again" — and that sentence is always
 * better than anything this file could invent, so it is used whenever there is
 * one. The two exceptions are the codes where ODIN's own wording is
 * deliberately terse: a rate limit, where the generic "try again" is actively
 * wrong because retrying is what keeps the limit closed, and a refusal, where
 * retrying will never work.
 */
function collectErrorText(error: unknown): string {
  if (error instanceof ApiError && error.status === 429) {
    return "You have taken several snapshots of this server in the last few minutes. Wait a moment and try again — ODIN limits this so one busy server cannot slow down the whole platform.";
  }
  if (error instanceof ApiError && error.status === 403) {
    return error.message || "Your role in this workspace cannot take snapshots. Ask an owner or admin to take one.";
  }
  if (error instanceof ApiError && error.message) return error.message;
  return "The snapshot did not complete. Please try again in a moment.";
}

function statusTone(status: SnapshotStatus): StatusTone {
  switch (status) {
    case "COMPLETED":
      return "ok";
    case "FAILED":
      return "danger";
    case "STALE":
      return "warn";
    default:
      return "neutral";
  }
}

export function Snapshot(): JSX.Element {
  const ctx = useAppContext();
  const [list, setList] = useState<SnapshotSummary[] | null>(null);
  const [detail, setDetail] = useState<SnapshotDetail | null>(null);
  const [bots, setBots] = useState<BotInstance[]>([]);
  const [botId, setBotId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Why a collection did not happen — kept apart from `error`.
   *
   * `error` is rendered as "can't reach the ODIN control API", which is the
   * right thing to say when a *load* fails. It is the wrong thing to say when
   * the API answered perfectly and told us ODIN is not in the Discord server:
   * that heading sends the customer to check ODIN's status page instead of
   * their own server, and it contradicts the sentence printed directly beneath
   * it.
   */
  const [collectError, setCollectError] = useState<string | null>(null);

  const ready = ctx.tenantId && ctx.guildId;

  const loadList = () => {
    if (!ctx.tenantId || !ctx.guildId) return;
    setError(null);
    api
      .listSnapshots(ctx.tenantId, ctx.guildId)
      .then((r) => {
        setList(r.snapshots);
        const newest = r.snapshots[0];
        if (newest) open(newest.id);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : "Could not load snapshots"));
  };

  const open = (id: string) => {
    if (!ctx.tenantId || !ctx.guildId) return;
    api
      .getSnapshot(ctx.tenantId, ctx.guildId, id)
      .then(setDetail)
      .catch((e) => setError(e instanceof ApiError ? e.message : "Could not load snapshot"));
  };

  useEffect(() => {
    if (!ready) {
      setList(null);
      setDetail(null);
      return;
    }
    loadList();
    if (ctx.tenantId) {
      api
        .listBotInstances(ctx.tenantId)
        .then((r) => setBots(r.botInstances))
        .catch(() => setBots([]));
    }
  }, [ctx.tenantId, ctx.guildId]); // loaders read the same context values

  /**
   * Take a snapshot.
   *
   * `botId` is optional and the guard no longer requires it. The collection is
   * done by ODIN's shared reader whichever instance is named; the id is
   * provenance, recorded so a customer running several instances can tell which
   * one they attributed the read to. Requiring it meant a workspace with no bot
   * instance — which is most of them, since one is not needed to read — could
   * never press this button at all.
   */
  const refresh = async () => {
    if (!ctx.tenantId || !ctx.guildId) return;
    setBusy(true);
    setCollectError(null);
    try {
      const snap = await api.createSnapshot(ctx.tenantId, ctx.guildId, botId || null);
      setDetail(snap);
      loadList();
    } catch (e) {
      setCollectError(collectErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title="Server snapshot">
        <p>
          A read-only capture of your Discord server exactly as it is now. This is the{" "}
          <strong>CURRENT</strong> state — separate from the <Link to="/app/blueprints">DESIRED</Link> blueprint and any{" "}
          <Link to="/app/dry-run">PROPOSED</Link> dry-run changes.
        </p>
      </PageHeader>

      {!apiConfigured ? (
        <NotConfiguredState title="Control API URL not configured">
          Set <code>VITE_CONTROL_API_URL</code> to collect snapshots.
        </NotConfiguredState>
      ) : (
        <>
          <ContextBar requireGuild />
          {!ready ? (
            <EmptyState title="Select a workspace and server">Choose a workspace and enter your ODIN server id above.</EmptyState>
          ) : (
            <>
              {/*
                "Create snapshot", not "Refresh snapshot". The old title read as
                a sibling of "Refresh server" on the Servers page, which does
                something else entirely — it re-reads Discord and stores
                nothing. This one writes a permanent row, and the two were being
                confused with each other.
              */}
              <Card title="Create snapshot" description="Record your server's structure as it is now. ODIN only reads — it never edits your server. Your earlier snapshots are kept.">
                <div className="row">
                  <label className="field">
                    <span className="field__label">Bot instance (optional)</span>
                    <select className="field__input" value={botId} onChange={(e) => setBotId(e.target.value)} aria-label="Bot instance">
                      <option value="">Let ODIN choose</option>
                      {bots.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.configuredName} ({b.status})
                        </option>
                      ))}
                    </select>
                  </label>
                  <Button variant="primary" disabled={busy} onClick={refresh}>
                    {busy ? "Collecting…" : "Create snapshot"}
                  </Button>
                </div>
              </Card>

              {collectError ? (
                <p className="ax-confirm__error" role="alert">
                  {collectError}
                </p>
              ) : null}
              {error ? <ApiUnavailableState detail={error} /> : null}
              {list === null ? <LoadingState label="Loading snapshots…" /> : null}

              {list && list.length > 0 ? (
                <Card title="Snapshot history">
                  <div className="op-list">
                    {list.map((s) => (
                      <button key={s.id} className="list-row" onClick={() => open(s.id)}>
                        <StatusIndicator tone={statusTone(s.status)} label={s.status} />
                        <span className="muted">
                          {s.roleCount} roles · {s.channelCount} channels · {formatAge(s.requestedAt)}
                        </span>
                        <code>{shortHash(s.snapshotHash)}</code>
                      </button>
                    ))}
                  </div>
                </Card>
              ) : list && list.length === 0 ? (
                <EmptyState title="No snapshots yet">Create a snapshot above to record your server&apos;s current structure.</EmptyState>
              ) : null}

              {detail ? <SnapshotView snapshot={detail} /> : null}
            </>
          )}
        </>
      )}
    </>
  );
}

function SnapshotView({ snapshot }: { snapshot: SnapshotDetail }): JSX.Element {
  const failed = snapshot.status === "FAILED";
  return (
    <StatePanel state="current">
      <Card title="Collection status">
        <div className="kv-grid">
          <KeyVal k="Status" v={<StatusIndicator tone={statusTone(snapshot.status)} label={snapshot.status} />} />
          <KeyVal k="Requested" v={formatAge(snapshot.requestedAt)} />
          <KeyVal k="Completed" v={snapshot.completedAt ? formatAge(snapshot.completedAt) : "—"} />
          <KeyVal k="Snapshot hash" v={shortHash(snapshot.snapshotHash)} mono />
        </div>
        {failed ? (
          <div className="dryrun-blocked" style={{ marginTop: "0.75rem" }}>
            <p>
              <strong>Collection failed{snapshot.failureCode ? ` (${snapshot.failureCode})` : ""}.</strong>{" "}
              {snapshot.failureSummary ?? "ODIN could not read the server."}
            </p>
            <p className="muted">
              Remediation: confirm the bot is still in the server, its token is valid, and it has the read permissions
              granted during onboarding, then refresh again.
            </p>
          </div>
        ) : null}
      </Card>

      {snapshot.content ? <SnapshotContentView content={snapshot.content} counts={snapshot} /> : null}
    </StatePanel>
  );
}

function SnapshotContentView({
  content,
  counts,
}: {
  content: NormalizedSnapshotContent;
  counts: Pick<SnapshotSummary, "roleCount" | "categoryCount" | "channelCount" | "overwriteCount">;
}): JSX.Element {
  const roles = [...(content.roles ?? [])].sort((a, b) => b.position - a.position);
  const categories = (content.channels ?? []).filter((c) => c.type === "category");
  const channels = (content.channels ?? []).filter((c) => c.type !== "category");
  const botPosition = content.bot?.highestRolePosition ?? 0;

  return (
    <>
      <Card title="Guild summary">
        <div className="kv-grid">
          <KeyVal k="Name" v={content.guild?.name ?? "—"} />
          <KeyVal k="Members (approx.)" v={content.guild?.approximateMemberCount ?? "—"} />
          <KeyVal k="Roles" v={counts.roleCount} />
          <KeyVal k="Categories" v={counts.categoryCount} />
          <KeyVal k="Channels" v={counts.channelCount} />
          <KeyVal k="Permission overwrites" v={counts.overwriteCount} />
          <KeyVal k="Bot highest role position" v={botPosition} />
        </div>
        <p className="section-note">
          The bot must sit <em>above</em> the roles it manages. Its highest role position is <strong>{botPosition}</strong>.
        </p>
      </Card>

      <Card title={`Roles (${roles.length})`}>
        <div className="table">
          <div className="table__head">
            <span>Position</span>
            <span>Name</span>
            <span>Markers</span>
          </div>
          {roles.map((r) => (
            <div className="table__row" key={r.discordId}>
              <span className="mono">{r.position}</span>
              <span>{r.name}</span>
              <span className="markers">
                {r.isEveryone ? <span className="marker">@everyone</span> : null}
                {r.managed ? <span className="marker marker--protected" title="Managed by an integration">managed</span> : null}
                {r.isBotManaged ? <span className="marker marker--protected">bot</span> : null}
              </span>
            </div>
          ))}
        </div>
      </Card>

      <Card title={`Categories (${categories.length})`}>
        {categories.length === 0 ? (
          <p className="muted">No categories.</p>
        ) : (
          <div className="table">
            <div className="table__head">
              <span>Position</span>
              <span>Name</span>
              <span>Overwrites</span>
            </div>
            {categories.map((c) => (
              <ChannelRow key={c.discordId} channel={c} />
            ))}
          </div>
        )}
      </Card>

      <Card title={`Channels (${channels.length})`}>
        <div className="table">
          <div className="table__head">
            <span>Position</span>
            <span>Name</span>
            <span>Overwrites</span>
          </div>
          {channels.map((c) => (
            <ChannelRow key={c.discordId} channel={c} />
          ))}
        </div>
      </Card>

      <p className="section-note">
        Everything above is <StateTag state="current" />. To define a target shape, open{" "}
        <Link to="/app/blueprints">Blueprints</Link>; to preview changes, run a <Link to="/app/dry-run">dry-run</Link>.
      </p>
    </>
  );
}

function ChannelRow({ channel }: { channel: NormalizedChannel }): JSX.Element {
  const ow = channel.overwrites?.length ?? 0;
  return (
    <div className="table__row">
      <span className="mono">{channel.position}</span>
      <span>
        <span className="muted">#</span>
        {channel.name} <span className="muted">· {channel.type}</span>
        {channel.isSystem ? <span className="marker marker--protected" title="Discord system channel">system</span> : null}
        {channel.nsfw ? <span className="marker">nsfw</span> : null}
      </span>
      <span>{ow > 0 ? <span className="marker marker--destructive" title="Has permission overwrites">{ow} overwrite{ow === 1 ? "" : "s"}</span> : <span className="muted">inherits</span>}</span>
    </div>
  );
}
